import React, { useState, useEffect } from 'react';
import { Table, Pagination, Button, ButtonGroup, Modal, Form } from 'react-bootstrap';
import { PencilFill, TrashFill } from 'react-bootstrap-icons';
import APIserver from '../../../../../api/axiosConfig';
import Swal from 'sweetalert2';

function PendienteTable({ bancoId }) {
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [pagos, setPagos] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [pagoToEdit, setPagoToEdit] = useState(null);
    const [monto, setMonto] = useState('');
    const [observaciones, setObservaciones] = useState('');
    const [fecha, setFecha] = useState('');

    useEffect(() => {
        fetchPagosPendientes();
    }, [rowsPerPage, currentPage, bancoId]);

    // Al montar el componente, establecer la fecha actual
    useEffect(() => {
        const today = new Date();
        const formattedDate = today.toISOString().split('T')[0]; // Convierte la fecha en formato YYYY-MM-DD
        setFecha(formattedDate); // Establece la fecha actual en el estado
    }, []); // Se ejecuta solo una vez al montar el componente

    const fetchPagosPendientes = async () => {
        setIsLoading(true);
        try {
            const response = await APIserver.get(`/api/mostrar/pendientes`, {
                params: {
                    limit: rowsPerPage,
                    offset: (currentPage - 1) * rowsPerPage,
                    banco: bancoId,
                }
            });
            const { results, hasMore } = response.data;
            setPagos(results);
            setHasMore(hasMore);
        } catch (error) {
            console.error('Error al obtener los pagos pendientes:', error);
        } finally {
            setIsLoading(false);
        }
    };

 

    const handleEdit = (pago) => {
        setPagoToEdit(pago);
        setMonto(pago.monto);
        setObservaciones(pago.observaciones);

        // Convertir la fecha a formato YYYY-MM-DD
        const formattedDate = formatDate(pago.fecha);
        setFecha(formattedDate); // Establece la fecha formateada en el estado

        setShowModal(true);
    };

    const handleSave = async () => {
        try {
            const updatedPago = { monto, observaciones, fecha };
            const response = await APIserver.put(`/api/pendientes/${pagoToEdit.id}`, updatedPago);

            if (response.status === 200) {
                await Swal.fire({
                    icon: 'success',
                    title: 'Éxito',
                    text: 'El pago ha sido actualizado correctamente.',
                    timer: 2000,
                    timerProgressBar: true,
                    showConfirmButton: false
                });
                fetchPagosPendientes(); // Recargar pagos
                setShowModal(false); // Cerrar el modal
            } else {
                Swal.fire({
                    icon: 'warning',
                    title: 'Advertencia',
                    text: 'No se realizaron cambios en el pago.',
                });
            }
        } catch (error) {
            console.error('Error al actualizar el pago:', error);

            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.response?.data?.message || 'Hubo un problema al actualizar el pago.',
            });
        }
    };

    // Función de eliminar el pago pendiente
    const handleEliminarPago = (idPago, monto) => {
        Swal.fire({
            title: '¿Estás seguro?',
            text: `Vas a eliminar un pago pendiente de ${monto.toLocaleString()}`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await APIserver.delete(`/api/eliminar/pago/${idPago}`);
                    // Actualizamos la tabla
                    fetchPagosPendientes();
                    Swal.fire('Eliminado!', 'El pago pendiente ha sido eliminado.', 'success');
                } catch (error) {
                    console.error('Error al eliminar el pago pendiente:', error);
                    Swal.fire('Error', 'Hubo un problema al eliminar el pago pendiente.', 'error');
                }
            }
        });
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const day = String(date.getUTCDate()).padStart(2, '0');
        const month = String(date.getUTCMonth() + 1).padStart(2, '0'); // Los meses son de 0 a 11
        const year = date.getUTCFullYear();
        return `${day}/${month}/${year}`;
    };
    
    return (
        <div style={{ boxShadow: "0px 0px 15px rgba(0, 0, 0, 0.1)" }}>
            <Table responsive className='text-center align-middle border'>
                <thead>
                    <tr>
                        <th>Monto</th>
                        <th>Detalle</th>
                        <th>Fecha</th>
                        <th>Acciones</th>
                    </tr>
                </thead>
                <tbody>
                    {isLoading ? (
                        <tr>
                            <td colSpan={4}>Cargando...</td>
                        </tr>
                    ) : pagos.length === 0 ? (
                        <tr>
                            <td colSpan={4}>No se encontraron pagos pendientes.</td>
                        </tr>
                    ) : (
                        pagos.map((pago) => (
                            <tr key={pago.id}>
                                <td>${pago.monto.toLocaleString()}</td>
                                <td>{pago.observaciones}</td>
                                <td>{formatDate(pago.fecha)}</td>
                                <td>
                                    <ButtonGroup aria-label='action-button'>
                                        <Button variant='link' className='text-warning' title='editar pago' onClick={() => handleEdit(pago)}>
                                            <PencilFill size={18} />
                                        </Button>
                                        <Button
                                            variant='link'
                                            className='text-danger'
                                            title='eliminar pago'
                                            onClick={() => handleEliminarPago(pago.id, pago.monto)}
                                        >
                                            <TrashFill size={18} />
                                        </Button>
                                    </ButtonGroup>
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
                <tfoot>
                    <tr>
                        <td colSpan={4}>
                            <div className='d-flex justify-content-center align-items-center'>
                                <Pagination>
                                    <Pagination.Prev disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)} />
                                    <Pagination.Item active>{currentPage}</Pagination.Item>
                                    <Pagination.Next disabled={!hasMore} onClick={() => setCurrentPage(currentPage + 1)} />
                                </Pagination>
                            </div>
                        </td>
                    </tr>
                </tfoot>
            </Table>

            {/* Modal para Editar Pago Pendiente */}
            <Modal show={showModal} onHide={() => setShowModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Editar Pago Pendiente</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group controlId="monto" className='mt-0 m-3'>
                            <Form.Label>Monto</Form.Label>
                            <Form.Control
                                type="number"
                                value={monto}
                                onChange={(e) => setMonto(e.target.value)}
                            />
                        </Form.Group>
                        <Form.Group controlId="observaciones" className='m-3'>
                            <Form.Label>Observaciones</Form.Label>
                            <Form.Control
                                type="text"
                                value={observaciones}
                                onChange={(e) => setObservaciones(e.target.value)}
                            />
                        </Form.Group>
                        <Form.Group controlId="fecha" className='m-3'>
                            <Form.Label>Fecha</Form.Label>
                            <Form.Control
                                type="date"
                                value={fecha}
                                onChange={(e) => setFecha(e.target.value)}
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="danger" onClick={() => setShowModal(false)}>
                        Cerrar
                    </Button>
                    <Button variant="dark" onClick={handleSave}>
                        Guardar Cambios
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default PendienteTable;
